/**
 * colorUtils.ts
 * Lightweight colour helpers used by the themed meeting components.
 * No external dependencies - pure math on hex strings.
 */

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

/** Returns an rgba() string from a hex colour + alpha 0-1. */
export function withAlpha(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Darkens a hex colour by multiplying each channel.
 *  @param pct 0 = original, 1 = black (default 0.20). */
export function darken(hex: string, pct = 0.2): string {
  const { r, g, b } = hexToRgb(hex);
  const m = (c: number) => Math.round(c * (1 - pct));
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}

/** Lightens a hex colour by mixing towards white.
 *  @param pct 0 = original, 1 = pure white (default 0.93 = very light tint). */
export function lighten(hex: string, pct = 0.93): string {
  const { r, g, b } = hexToRgb(hex);
  const m = (c: number) => Math.round(c + (255 - c) * pct);
  return `rgb(${m(r)},${m(g)},${m(b)})`;
}
